import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { NgbCustomParserFormatter } from "components/common/date-picker/ngb-custom-parser-formatter";
import { AlertController } from "ionic-angular";
import CollapsibleEvent from "../../../models/events/collapsible-modal.event";
import { FacilitiesService } from "../../../providers/facilities-service/facilities.service";
import { UtilityService } from "../../../providers/utility-service/utility-service";

@Component({
  selector: "rates-facility",
  templateUrl: "rates-facility.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RatesFacility),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RatesFacility),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class RatesFacility implements OnInit, OnDestroy {
  @Input() currentFacility: any;
  @Output() onCanceled = new EventEmitter<any>();
  @Output() onFacilityRateSaveSuccess = new EventEmitter<any>();

  careLevels: any[] = [
    { name: "Level 1", level: "1", description: "" },
    { name: "Level 2", level: "2", description: "" },
    { name: "Level 3", level: "3", description: "" },
    { name: "Level 4", level: "4", description: "" },
    { name: "Level 5", level: "5", description: "" },
  ];

  careLevelShowMap: Map<string, boolean> = new Map<string, boolean>([
    ["1", false],
    ["2", false],
    ["3", false],
    ["4", false],
    ["5", false],
  ]);

  public onCollapseClicked($e: CollapsibleEvent): void {
    const level = $e.controlId.split("-").pop();
    this.careLevelShowMap[level] = !this.careLevelShowMap[level];
  }

  /**
   * Accordion header for a care level: "Level 1 - Semi-private" — or just
   * "Level 1" when it has no description.
   *
   * retrieveCareLevels() assigns `this.careLevels = response` straight from
   * GET /facility/care-levels with no per-item normalisation, so every field here
   * is whatever the API sent. The template used to call
   * `careLevel.description.trim()` directly, which throws
   * "Cannot read properties of undefined (reading 'trim')" on EVERY change-detection
   * pass for a level with no description — the *ngFor renders nothing and the whole
   * Rates tab is dead (verified against dev1). The CareLevel interface types
   * `description` as a required string, but manage-care-levels — the other consumer of
   * this same endpoint — guards it anyway (`level.description ? ... : ""`), so the two
   * consumers disagree. Guarding here is the cheap side of that bet.
   */
  careLevelTitle(careLevel: any): string {
    if (!careLevel) {
      return "";
    }
    const name = careLevel.name || "";
    const description = (careLevel.description || "").trim();
    return description.length > 0 ? `${name} - ${description}` : name;
  }

  constructor(
    private facilityService: FacilitiesService,
    private utilService: UtilityService,
    private alertCtrl: AlertController
  ) {}

  ngOnInit() {
    this.retrieveCareLevels();
  }

  retrieveCareLevels() {
    this.showLoadingMessage();
    this.facilityService.getCareLevels().subscribe(
      (response) => {
        if (response != null && response.length > 0) {
          this.careLevels = response;
          this.careLevelShowMap = new Map<string, boolean>();
          this.careLevels.forEach((careLevel) => {
            this.careLevelShowMap.set(careLevel.level, false);
          });
        }
        this.utilService.decreaseSpinnerCount();
      },
      () => {
        this.onFailure("Failed to retrieve Care Levels, please retry.");
      }
    );
  }

  ngOnDestroy() {}

  cancel() {
    this.onCanceled.emit(true);
  }

  facilityRateSaveSuccess() {
    this.onFacilityRateSaveSuccess.emit(true);
  }

  private showLoadingMessage() {
    this.utilService.increaseSpinnerCount("Loading details...");
  }

  onFailure(msg: string) {
    this.utilService.decreaseSpinnerCount();
    const alert = this.alertCtrl.create({
      title: "Error Message",
      message: msg,
      buttons: [{ text: "OK" }],
    });
    alert.present();
  }
}



// WEBPACK FOOTER //
// ./src/components/facilities/rates-facility/rates-facility.ts